import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Search, Eye, FileText, Award, TrendingUp, Calendar, Filter, ArrowRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

const PartnerBeneficiaries: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const companyId = localStorage.getItem("partnerCompanyId") || "1";
  const companyType = localStorage.getItem("partnerCompanyType") || "aviation";
  const isAviation = companyType === "aviation";
  
  // Função para calcular cashback baseado na emissão de CO2 (50 MSC por kg)
  const calcularCashback = (emissaoStr: string): number => {
    const emissaoNum = parseFloat(emissaoStr.replace(" kg", "").replace(",", ".").trim());
    if (isNaN(emissaoNum)) return 0;
    return emissaoNum * 50;
  };

  // Função para calcular CO2 total de um beneficiário
  const calcularCO2Total = (beneficiary: any): number => {
    let totalCO2Kg = 0;
    
    if (isAviation && beneficiary.viagens) {
      beneficiary.viagens.forEach((viagem: any) => {
        const emissaoStr = viagem.emissao?.replace(" kg", "").replace(",", ".").trim() || "0";
        totalCO2Kg += parseFloat(emissaoStr) || 0;
      });
    } else if (!isAviation && beneficiary.checkIns) {
      beneficiary.checkIns.forEach((checkIn: any) => {
        totalCO2Kg += checkIn.emissao || 0;
      });
    }
    
    return totalCO2Kg / 1000; // Converter para toneladas
  };

  // Função para calcular mudas (0.15 t CO2 por árvore)
  const calcularMudas = (co2Toneladas: number): number => {
    return Math.ceil(co2Toneladas / 0.15);
  };

  // Mock data - Em produção, viria de uma API com filtro WHERE empresa_id = X
  const beneficiaries = [
    {
      id: 1,
      nome: "João Silva",
      email: "joao@email.com",
      cpf: "123.456.789-00",
      ultimaViagem: "2024-01-15",
      totalRecebido: 1250.50,
      status: "Ativo",
      viagens: [
        { id: "1", data: "15/06/2024", rota: "GRU - SDU", distancia: "685 km", emissao: "0,185 kg" },
        { id: "2", data: "20/07/2024", rota: "SDU - GRU", distancia: "685 km", emissao: "0,420 kg" }
      ],
      checkIns: []
    },
    {
      id: 3,
      nome: "Pedro Oliveira",
      email: "pedro@email.com",
      cpf: "456.789.123-00",
      ultimaViagem: "2023-12-20",
      totalRecebido: 2100.00,
      status: "Inativo",
      viagens: [
        { id: "1", data: "15/06/2024", rota: "GRU - SDU", distancia: "685 km", emissao: "0,185 kg" },
        { id: "2", data: "20/07/2024", rota: "SDU - GRU", distancia: "685 km", emissao: "0,420 kg" },
        { id: "3", data: "25/08/2024", rota: "GRU - BSB", distancia: "1015 km", emissao: "0,550 kg" }
      ],
      checkIns: []
    },
    {
      id: 4,
      nome: "Ana Costa",
      email: "ana@email.com",
      cpf: "789.123.456-00",
      ultimaViagem: "2024-01-12",
      totalRecebido: 675.75,
      status: "Ativo",
      viagens: [],
      checkIns: [
        { id: "1", data: "05/03/2024", origem: "Brasília, DF", transporte: "Aéreo Convencional", distancia: 1060, emissao: 265.0, moedas: 2650.0 },
        { id: "2", data: "18/03/2024", origem: "Curitiba, PR", transporte: "Carro", distancia: 410, emissao: 77.9, moedas: 779.0 }
      ]
    }
  ];

  // Calcular métricas de impacto para cada beneficiário
  const beneficiariesWithMetrics = beneficiaries.map(beneficiary => {
    const co2Toneladas = calcularCO2Total(beneficiary);
    const mudas = calcularMudas(co2Toneladas);
    const isTopTier = beneficiary.totalRecebido > 1500; // Top tier se recebeu mais de 1500 MSC
    
    return {
      ...beneficiary,
      co2Toneladas,
      mudas,
      isTopTier
    };
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<string>("recentes");
  const [showTopOnly, setShowTopOnly] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<any>(null);
  const [isExtratoOpen, setIsExtratoOpen] = useState(false);

  // Filtrar e ordenar beneficiários
  let filteredBeneficiaries = beneficiariesWithMetrics.filter(
    (beneficiary) =>
      beneficiary.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      beneficiary.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      beneficiary.cpf.includes(searchTerm)
  );

  // Filtrar apenas top beneficiários se ativado
  if (showTopOnly) {
    filteredBeneficiaries = filteredBeneficiaries.filter(b => b.isTopTier);
  }

  // Ordenar
  if (sortBy === "impacto") {
    filteredBeneficiaries.sort((a, b) => b.co2Toneladas - a.co2Toneladas);
  } else if (sortBy === "recentes") {
    filteredBeneficiaries.sort((a, b) => 
      new Date(b.ultimaViagem).getTime() - new Date(a.ultimaViagem).getTime()
    );
  } else if (sortBy === "msc") {
    filteredBeneficiaries.sort((a, b) => b.totalRecebido - a.totalRecebido);
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR");
  };

  const handleViewExtrato = (beneficiary: any) => {
    setSelectedBeneficiary(beneficiary);
    setIsExtratoOpen(true);
  };

  const handleGenerateCertificate = (beneficiary: any) => {
    // Em produção, geraria um PDF real
    toast({
      title: "Certificado gerado!",
      description: `Certificado de ${beneficiary.nome} está sendo baixado...`,
    });
  };

  const handleViewMore = (beneficiary: any) => {
    // Navegar para página de detalhes do beneficiário
    navigate(`/partner/beneficiaries/${beneficiary.id}`, {
      state: { beneficiary, companyId, companyType }
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Meus Beneficiários</h1>
        <p className="text-gray-600 mt-1">Gerencie seus clientes e identifique seus Super Clientes Sustentáveis</p>
      </div>

      {/* Filtros de Topo */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex flex-wrap gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Ordenar por" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recentes">Mais Recentes</SelectItem>
                  <SelectItem value="impacto">Maior Impacto (CO₂)</SelectItem>
                  <SelectItem value="msc">Mais MSC Recebido</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                variant={showTopOnly ? "default" : "outline"}
                onClick={() => setShowTopOnly(!showTopOnly)}
                className={showTopOnly ? "bg-[#0e7cb4] text-white" : ""}
              >
                <Award className="mr-2 h-4 w-4" />
                Somente Top Beneficiários
              </Button>
            </div>

            <div className="relative w-full md:w-auto md:min-w-[300px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="search"
                placeholder="Buscar por nome, email ou CPF..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Clientes Beneficiados</CardTitle>
              <CardDescription>
                Total: {filteredBeneficiaries.length} {filteredBeneficiaries.length === 1 ? "cliente" : "clientes"}
                {showTopOnly && " (Top Beneficiários)"}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>CPF</TableHead>
                  <TableHead>Última {isAviation ? "Viagem" : "Check-in"}</TableHead>
                  <TableHead className="text-center">CO₂ Compensado</TableHead>
                  <TableHead className="text-center">Árvores</TableHead>
                  <TableHead className="text-right">Total Recebido (MSC)</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBeneficiaries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-gray-500 py-8">
                      Nenhum beneficiário encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredBeneficiaries.map((beneficiary) => (
                    <TableRow key={beneficiary.id} className={beneficiary.isTopTier ? "bg-yellow-50/50" : ""}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className={beneficiary.isTopTier ? "bg-yellow-100 text-yellow-700" : "bg-[#0e7cb4] text-white"}>
                                {getInitials(beneficiary.nome)}
                              </AvatarFallback>
                            </Avatar>
                            {beneficiary.isTopTier && (
                              <div className="absolute -top-1 -right-1 bg-yellow-400 rounded-full p-0.5">
                                <Award className="h-3 w-3 text-yellow-800" />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              {beneficiary.nome}
                              {beneficiary.isTopTier && (
                                <Badge variant="outline" className="text-xs bg-yellow-100 text-yellow-700 border-yellow-300">
                                  Top Cliente
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{beneficiary.email}</TableCell>
                      <TableCell>{beneficiary.cpf}</TableCell>
                      <TableCell>{formatDate(beneficiary.ultimaViagem)}</TableCell>
                      <TableCell className="text-center font-medium">
                        {beneficiary.co2Toneladas > 0 
                          ? `${beneficiary.co2Toneladas.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })} t`
                          : "--"
                        }
                      </TableCell>
                      <TableCell className="text-center">
                        {beneficiary.mudas > 0 ? (
                          <span className="flex items-center justify-center gap-1 font-medium text-green-600">
                            🌳 {beneficiary.mudas}
                          </span>
                        ) : (
                          "--"
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {beneficiary.totalRecebido.toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })} MSC
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            beneficiary.status === "Ativo"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-xs px-2 py-0.5"
                              : "bg-slate-50 text-slate-600 border-slate-200 text-xs px-2 py-0.5"
                          }
                        >
                          {beneficiary.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleViewExtrato(beneficiary)}
                            title="Ver Extrato"
                          >
                            <Eye className="h-4 w-4 text-[#0e7cb4]" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleGenerateCertificate(beneficiary)}
                            title="Gerar Certificado"
                          >
                            <FileText className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewMore(beneficiary)}
                            className="bg-[#0e7cb4] text-white hover:bg-[#0a6a9a] border-[#0e7cb4]"
                          >
                            Ver mais
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Sheet para Extrato */}
      <Sheet open={isExtratoOpen} onOpenChange={setIsExtratoOpen}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Extrato de {selectedBeneficiary?.nome}</SheetTitle>
            <SheetDescription>
              Últimas 5 {isAviation ? "viagens" : "transações"} com {localStorage.getItem("partnerCompanyName")}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            {selectedBeneficiary && (
              <>
                {isAviation && selectedBeneficiary.viagens && selectedBeneficiary.viagens.slice(0, 5).map((viagem: any, index: number) => (
                  <Card key={index}>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <span className="text-sm font-medium">{viagem.data}</span>
                        </div>
                        <Badge variant="outline" className="text-xs">Viagem</Badge>
                      </div>
                      <div className="space-y-1 text-sm">
                        <p><strong>Rota:</strong> {viagem.rota}</p>
                        <p><strong>Distância:</strong> {viagem.distancia}</p>
                        <p><strong>Emissão:</strong> {viagem.emissao}</p>
                        <p className="text-green-600 font-medium">
                          <strong>MSC Gerado:</strong> {calcularCashback(viagem.emissao || "0 kg").toFixed(2)} MSC
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {!isAviation && selectedBeneficiary.checkIns && selectedBeneficiary.checkIns.slice(0, 5).map((checkIn: any, index: number) => (
                  <Card key={index}>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <span className="text-sm font-medium">{checkIn.data}</span>
                        </div>
                        <Badge variant="outline" className="text-xs">Check-in</Badge>
                      </div>
                      <div className="space-y-1 text-sm">
                        <p><strong>Origem:</strong> {checkIn.origem}</p>
                        <p><strong>Transporte:</strong> {checkIn.transporte}</p>
                        <p><strong>Distância:</strong> {checkIn.distancia} km</p>
                        <p><strong>Emissão:</strong> {checkIn.emissao.toFixed(2)} kg CO₂</p>
                        <p className="text-green-600 font-medium">
                          <strong>MSC Gerado:</strong> {checkIn.moedas?.toFixed(2) || "0.00"} MSC
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {(!selectedBeneficiary.viagens || selectedBeneficiary.viagens.length === 0) && 
                 (!selectedBeneficiary.checkIns || selectedBeneficiary.checkIns.length === 0) && (
                  <p className="text-center text-gray-500 py-8">Nenhuma transação encontrada</p>
                )}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default PartnerBeneficiaries;
